import type { BeatData, PeakData } from "./peaks.js";

export interface WaveformTheme {
  wave: string;
  waveDim: string;
  playhead: string;
  beat: string;
  downbeat: string;
  cue: string;
  background: string;
}

export const THEME: WaveformTheme = {
  wave: "#6ea8fe",
  waveDim: "#3b5378",
  playhead: "#f87171",
  beat: "rgba(255, 255, 255, 0.22)",
  downbeat: "rgba(255, 255, 255, 0.55)",
  cue: "#fbbf24",
  background: "#0e0f12",
};

/** Resize the backing store to the element's CSS size. Returns false if unsized. */
export function fitToDisplay(canvas: HTMLCanvasElement): boolean {
  const ratio = window.devicePixelRatio || 1;
  const width = Math.round(canvas.clientWidth * ratio);
  const height = Math.round(canvas.clientHeight * ratio);
  if (width === 0 || height === 0) return false;
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  return true;
}

/**
 * Draw the whole track into an offscreen canvas once per load.
 *
 * The overview never changes while a track is loaded, so redrawing 2000 buckets
 * every frame would be wasted work - per-frame cost is then one blit plus a
 * playhead line.
 */
export function renderOverview(
  peaks: PeakData,
  width: number,
  height: number,
  color: string = THEME.waveDim,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  const mid = canvas.height / 2;
  const scale = mid * 0.95;
  ctx.fillStyle = color;

  for (let x = 0; x < canvas.width; x++) {
    const bucket = Math.min(peaks.count - 1, Math.floor((x / canvas.width) * peaks.count));
    const low = peaks.values[bucket * 2] ?? 0;
    const high = peaks.values[bucket * 2 + 1] ?? 0;
    const top = mid - (high / 127) * scale;
    const bottom = mid - (low / 127) * scale;
    ctx.fillRect(x, top, 1, Math.max(1, bottom - top));
  }
  return canvas;
}

export interface DetailView {
  peaks: PeakData;
  beats: BeatData | null;
  /** Playback position in seconds. */
  position: number;
  /** Seconds visible across the full canvas width. */
  window: number;
  cue: number | null;
}

/**
 * Draw the region around the playhead, with the playhead fixed at centre so the
 * waveform scrolls past it.
 */
export function renderDetail(canvas: HTMLCanvasElement, view: DetailView): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const { width, height } = canvas;
  const mid = height / 2;
  const scale = mid * 0.9;
  const pixelsPerSecond = width / view.window;
  const startTime = view.position - view.window / 2;

  ctx.fillStyle = THEME.background;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = THEME.wave;
  const { peaks } = view;
  for (let x = 0; x < width; x++) {
    const time = startTime + x / pixelsPerSecond;
    if (time < 0) continue;
    const bucket = Math.floor(time * peaks.bucketsPerSecond);
    if (bucket < 0 || bucket >= peaks.count) continue;
    const low = peaks.values[bucket * 2] ?? 0;
    const high = peaks.values[bucket * 2 + 1] ?? 0;
    const top = mid - (high / 127) * scale;
    const bottom = mid - (low / 127) * scale;
    ctx.fillRect(x, top, 1, Math.max(1, bottom - top));
  }

  // Beat markers (P8). Absent entirely when no tempo could be asserted.
  const beats = view.beats?.beats;
  if (beats && beats.length > 0) {
    const endTime = startTime + view.window;
    // Beats are sorted, so binary search the window start rather than scanning
    // a 2500-entry array every frame.
    let index = lowerBound(beats, startTime);
    let ordinal = index;
    while (index < beats.length) {
      const time = beats[index] as number;
      if (time > endTime) break;
      const x = Math.round((time - startTime) * pixelsPerSecond);
      // Every fourth beat drawn brighter as a bar line. This assumes 4/4 from
      // an arbitrary start - it is a reading aid, not a downbeat claim.
      ctx.fillStyle = ordinal % 4 === 0 ? THEME.downbeat : THEME.beat;
      ctx.fillRect(x, 0, 1, height);
      index++;
      ordinal++;
    }
  }

  if (view.cue !== null) {
    const x = Math.round((view.cue - startTime) * pixelsPerSecond);
    if (x >= 0 && x <= width) {
      ctx.fillStyle = THEME.cue;
      ctx.fillRect(x, 0, 2, height);
    }
  }

  ctx.fillStyle = THEME.playhead;
  ctx.fillRect(Math.round(width / 2), 0, 2, height);
}

function lowerBound(values: Float64Array, target: number): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if ((values[mid] as number) < target) low = mid + 1;
    else high = mid;
  }
  return low;
}
